import axios from 'axios';

import { API, DOMAIN } from '@autopass-web/domain';

import type {
  ApsGetOrderQuery,
  AuthApsApplePayQuery,
  AuthApsCreditCardQuery,
  AuthApsWalletPayQuery,
  CreateApplePaySessionValidtionQuery,
  CreateCreditCardSessionQuery,
  CreateLinePaySessionQuery,
  CreateWalletSessionQuery,
} from './request-types';
import type {
  ApsGetOrderResponse,
  AuthApsWalletPayResponse,
  ConfirmCreditCardTransactionResponse,
  CreateCreditCardSessionResponse,
  CreateLinePaySessionResponse,
  CreateParkingTicketResponse,
  CreateWalletSessionResponse,
  GetBulletinBoardsResponse,
  GetParkinglotFromCodeResponse,
  GetParkinglotFromPoiResponse,
  GetParkingRecordBillsResponse,
} from './response-types';

const API_CONFIG = DOMAIN.API_URL[API.USER];

export const axiosInstance = axios.create({
  headers: API_CONFIG.headers,
  baseURL: API_CONFIG.baseUrl,
  timeout: 60 * 1000,
  validateStatus: (status) => {
    return status >= 200 && status < 300;
  },
});

/**
 * @swagger
 *
 * the document may not be comprehensive, please double-check the response value.
 * https://storage.googleapis.com/pklotcrop-swagger-doc/79455a04b9bb8e812379726aea734db9/dist/index.html?url=../api3.1666666703.json
 */

class Client {
  // User API
  fetchParkinglotBySerialCode = ({
    parkinglotCode,
    serialCode,
  }: {
    parkinglotCode: string;
    serialCode: string;
  }) =>
    axiosInstance.get<GetParkinglotFromCodeResponse>(
      `/parking_codes/${serialCode}`,
      {
        params: { serial_number: parkinglotCode },
      },
    );
  fetchPoiById = ({ poiId }: { poiId: string }) =>
    axiosInstance.get<GetParkinglotFromPoiResponse>(`/pois/${poiId}`);
  fetchBulletinBoards = ({
    codes,
    partnerId,
    distributor,
  }: {
    codes: string;
    partnerId?: string;
    distributor?: string;
  }) =>
    axiosInstance.get<GetBulletinBoardsResponse>(`/bulletin_boards`, {
      params: { codes, partner_id: partnerId, distributor },
    });
  fetchParkingRecordBills = ({
    parkinglotId,
    plateNumberFuzzy,
    vehicleType,
  }: {
    parkinglotId: string;
    plateNumberFuzzy: string;
    vehicleType: string;
  }) =>
    axiosInstance.get<GetParkingRecordBillsResponse>(
      `/parking_records/query_parking`,
      {
        params: {
          parkinglot_id: parkinglotId,
          plate_number_fuzzy: plateNumberFuzzy,
          vehicle_type: vehicleType,
        },
      },
    );

  createParkingTicket = ({
    parkingLotId,
    plateNumber,
    vehicleType,
    parkingRecordId,
    parkingRecordKioskId,
  }: {
    parkingLotId: string;
    plateNumber: string;
    vehicleType: string;
    parkingRecordId?: string;
    parkingRecordKioskId?: string;
  }) =>
    axiosInstance.post<CreateParkingTicketResponse>('/parking_tickets/create', {
      parkinglot_id: parkingLotId,
      plate_number: plateNumber,
      vehicle_type: vehicleType,
      parking_record_id: parkingRecordId,
      parking_record_kiosk_id: parkingRecordKioskId,
    } as {
      parkinglot_id: string;
      plate_number: string;
      vehicle_type: string;
      parking_record_id?: string;
      parking_record_kiosk_id?: string;
    });

  // payment APIs

  createApplePaySessionValidtion = ({
    transactionType,
    sessionUrl,
  }: CreateApplePaySessionValidtionQuery) =>
    axiosInstance.post<unknown>(`/apple_pay/session_validation`, {
      session_url: sessionUrl,
      transaction_type: transactionType,
    });
  createLinePaySession = ({
    parkingTicketId,
    callbackUrl,
    email,
    receipt,
  }: CreateLinePaySessionQuery) =>
    axiosInstance.post<CreateLinePaySessionResponse>(
      `/parking_tickets/line_pay/init`,
      {
        parking_ticket_id: parkingTicketId,
        email,
        receipt,
        callback_url: callbackUrl,
      },
    );

  createWalletSession = ({
    parkingTicketId,
    email,
    receipt,
    callbackUrl,
    walletType,
  }: CreateWalletSessionQuery) =>
    axiosInstance.post<CreateWalletSessionResponse>(
      `/parking_tickets/${walletType}/init`,
      {
        parking_ticket_id: parkingTicketId,
        email,
        type: 'web',
        callback_url: callbackUrl,
        receipt,
      },
    );

  createCreditCardSession = ({
    parkingTicketId,
    email,
    receipt,
  }: CreateCreditCardSessionQuery) =>
    axiosInstance.post<CreateCreditCardSessionResponse>(
      `/parking_tickets/${parkingTicketId}/create_transaction`,
      {
        // If email is not provided, [the value = null] is necessary
        email: email ?? null,
        receipt,
      },
    );

  authApsApplePay = ({
    parkingTicketId,
    applePayPaymentInfo,
    email,
    receipt,
  }: AuthApsApplePayQuery) =>
    axiosInstance.post(`/parking_tickets/apple_pay/pay`, {
      parking_ticket_id: parkingTicketId,
      apple_pay_payment_info: applePayPaymentInfo,
      billing_contact: {
        email_address: email,
      },
      type: 'web',
      receipt,
    });
  authApsCreditCard = ({
    parkingTicketTransactionId,
    token,
    transactionId,
  }: AuthApsCreditCardQuery) =>
    axiosInstance.post<ConfirmCreditCardTransactionResponse>(
      `/parking_tickets/credit_card/pay`,
      {
        parking_ticket_transaction_id: parkingTicketTransactionId,
        token,
        transaction_id: transactionId,
      },
    );

  authApsWalletPay = ({ walletType, transactionId }: AuthApsWalletPayQuery) =>
    axiosInstance.post<AuthApsWalletPayResponse>(
      `/parking_tickets/${walletType}/confirm`,
      {
        transaction_id: transactionId,
      },
    );

  apsGetOrder = ({ parkingTicketId, parkingOrderId }: ApsGetOrderQuery) =>
    axiosInstance.get<ApsGetOrderResponse>(
      `/parking_tickets/${parkingTicketId}/orders/${parkingOrderId}`,
    );

  validateBarcode = ({ barcode }: { barcode: string }) =>
    axiosInstance.get<{ data: { status: 'valid' } }>(
      `/einvoice/validate_mobile_barcode?barcode=${encodeURIComponent(
        barcode,
      )}`,
    );
}

export default new Client();
